import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { Users, Mail, Send, RefreshCw, UserCheck, Clock, X, ChevronDown, Shield } from 'lucide-react';
import toast from 'react-hot-toast';

interface AdminUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
  invited_at: string | null;
  user_metadata: Record<string, unknown>;
  roles: string[];
}

const AVAILABLE_ROLES = [
  { id: 'admin', label: 'Admin', color: 'bg-red-100 text-red-700 border-red-200' },
  { id: 'events', label: 'Events', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { id: 'messages', label: 'Messages', color: 'bg-green-100 text-green-700 border-green-200' },
  { id: 'reading', label: 'Reading', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { id: 'members', label: 'Members', color: 'bg-teal-100 text-teal-700 border-teal-200' },
];

const getRoleColor = (role: string) =>
  AVAILABLE_ROLES.find(r => r.id === role)?.color ?? 'bg-gray-100 text-gray-700 border-gray-200';

const AdminMembers: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [savingRoles, setSavingRoles] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const getToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? '';
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed to fetch users');
      setUsers(data.users ?? []);
    } catch (err) {
      console.error('Failed to load members:', err);
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const token = await getToken();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-users`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: inviteEmail.trim() }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed to send invite');
      toast.success(`Invite sent to ${inviteEmail.trim()}`);
      setInviteEmail('');
      setShowInviteForm(false);
      fetchUsers();
    } catch (err) {
      console.error('Failed to send invite:', err);
      toast.error('Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const toggleRole = async (userId: string, role: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    const currentRoles = user.roles || [];
    const newRoles = currentRoles.includes(role)
      ? currentRoles.filter(r => r !== role)
      : [...currentRoles, role];

    setUsers(prev => prev.map(u =>
      u.id === userId ? { ...u, roles: newRoles } : u
    ));

    setSavingRoles(userId);
    try {
      const token = await getToken();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-users`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ user_id: userId, roles: newRoles }),
        }
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error ?? 'Failed to update roles');
      }
    } catch (err) {
      console.error('Failed to update roles:', err);
      toast.error('Failed to update roles');
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, roles: currentRoles } : u
      ));
    } finally {
      setSavingRoles(null);
    }
  };

  const confirmedUsers = users.filter(u => u.email_confirmed_at);
  const pendingUsers = users.filter(u => !u.email_confirmed_at);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <div className="flex items-center space-x-3">
            <Users className="w-6 sm:w-7 h-6 sm:h-7 text-gray-900" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Members</h1>
          </div>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Manage registered users, roles, and invites</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowInviteForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 whitespace-nowrap"
          >
            <Send className="w-4 h-4" />
            <span>Send Invite</span>
          </button>
        </div>
      </div>

      {showInviteForm && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">Invite New Member</h3>
            <button
              onClick={() => { setShowInviteForm(false); setInviteEmail(''); }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="Enter email address"
              required
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            <button
              type="submit"
              disabled={inviting || !inviteEmail.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" />
              {inviting ? 'Sending...' : 'Send Invite'}
            </button>
          </form>
          <p className="text-xs text-gray-500 mt-2">
            An invitation email will be sent to this address with a link to set up their account.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4 sm:p-5">
          <div className="flex items-center gap-3">
            <Users className="w-7 h-7 text-blue-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-blue-600">Total Users</p>
              <p className="text-2xl font-bold text-blue-900">{users.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 sm:p-5">
          <div className="flex items-center gap-3">
            <UserCheck className="w-7 h-7 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-green-600">Confirmed</p>
              <p className="text-2xl font-bold text-green-900">{confirmedUsers.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 sm:p-5">
          <div className="flex items-center gap-3">
            <Clock className="w-7 h-7 text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-amber-600">Pending Invite</p>
              <p className="text-2xl font-bold text-amber-900">{pendingUsers.length}</p>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow p-12 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : users.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No members found.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-700">All Members ({users.length})</h3>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Shield className="w-3.5 h-3.5" />
              <span>Click roles to assign</span>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {users.map(user => (
              <div key={user.id} className="px-4 sm:px-6 py-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-700 font-semibold text-sm">
                        {user.email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-gray-900 break-all">{user.email}</p>
                        {!user.email_confirmed_at && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">
                            Pending
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
                        <p className="text-xs text-gray-500">
                          Joined {new Date(user.created_at).toLocaleDateString()}
                        </p>
                        {user.last_sign_in_at && (
                          <p className="text-xs text-gray-400">
                            Last seen {new Date(user.last_sign_in_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <a
                      href={`mailto:${user.email}`}
                      className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 transition-colors whitespace-nowrap"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      Email
                    </a>
                  </div>
                </div>

                {/* Roles multi-select */}
                <div className="mt-3 relative" ref={openDropdown === user.id ? dropdownRef : undefined}>
                  <div className="flex items-center gap-2 flex-wrap">
                    {user.roles.length > 0 ? (
                      user.roles.map(role => {
                        const roleDef = AVAILABLE_ROLES.find(r => r.id === role);
                        return (
                          <span
                            key={role}
                            className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getRoleColor(role)}`}
                          >
                            {roleDef?.label ?? role}
                          </span>
                        );
                      })
                    ) : (
                      <span className="text-xs text-gray-400 italic">No roles assigned</span>
                    )}
                    <button
                      onClick={() => setOpenDropdown(openDropdown === user.id ? null : user.id)}
                      disabled={savingRoles === user.id}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full border border-gray-200 transition-colors disabled:opacity-50"
                    >
                      {savingRoles === user.id ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        <ChevronDown className={`w-3 h-3 transition-transform ${openDropdown === user.id ? 'rotate-180' : ''}`} />
                      )}
                      Edit Roles
                    </button>
                  </div>

                  {openDropdown === user.id && (
                    <div className="absolute z-20 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 left-0">
                      <div className="px-3 py-2 border-b border-gray-100">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Assign Roles</p>
                      </div>
                      {AVAILABLE_ROLES.map(role => {
                        const isActive = user.roles.includes(role.id);
                        return (
                          <button
                            key={role.id}
                            onClick={() => toggleRole(user.id, role.id)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                          >
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                              isActive
                                ? 'bg-blue-600 border-blue-600'
                                : 'border-gray-300'
                            }`}>
                              {isActive && (
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${role.color}`}>
                              {role.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMembers;
